import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import {
  PageContainer, DataTable, PremiumModal, PremiumCard, Button,
  Field, SelectField, StatusBadge, EmptyState,
} from "components/ui";

const Inventory = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const branch = user?.branch;

  const [stockItems, setStockItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [adjustModal, setAdjustModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [adjForm, setAdjForm] = useState({ quantityChange: "", reason: "Damaged", note: "" });
  const [adjSubmitting, setAdjSubmitting] = useState(false);

  useEffect(() => {
    if (!branch) return;
    (async () => {
      try {
        const res = await axiosPrivate.get(`/api/branches/${branch.id}/stock`);
        setStockItems(res.data);
      } catch (_) {
        toast.error("Failed to load inventory");
      } finally {
        setLoading(false);
      }
    })();
  }, [branch]);

  const openAdjustModal = (item) => {
    setSelectedItem(item);
    setAdjForm({ quantityChange: "", reason: "Damaged", note: "" });
    setAdjustModal(true);
  };

  const handleAdjustSubmit = async () => {
    if (!adjForm.quantityChange) {
      toast.warn("Enter a quantity change");
      return;
    }
    setAdjSubmitting(true);
    try {
      await axiosPrivate.post("/api/stock/adjust", {
        productId: selectedItem.product.id,
        quantityChange: parseFloat(adjForm.quantityChange),
        reason: adjForm.reason,
        note: adjForm.note,
      });
      toast.success("Stock adjusted");
      setAdjustModal(false);
      const res = await axiosPrivate.get(`/api/branches/${branch.id}/stock`);
      setStockItems(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || "Adjustment failed");
    } finally {
      setAdjSubmitting(false);
    }
  };

  const movementsBasePath = user?.role === "BRANCH_ADMIN" ? "/branch/movements" : "/stock/movements";

  if (!branch) {
    return (
      <PageContainer>
        <PremiumCard>
          <EmptyState title="No branch assigned" />
        </PremiumCard>
      </PageContainer>
    );
  }

  // Map to provide flat searchable fields for DataTable search
  const flatItems = stockItems.map((s) => ({
    ...s,
    _name: s.product?.name || "",
    _sku: s.product?.sku || "",
    _category: s.product?.category?.name || "",
  }));

  return (
    <PageContainer>
      <DataTable
        title={`Inventory · ${branch.name}`}
        subtitle="Live stock positions on this branch"
        loading={loading}
        data={flatItems}
        rowKey={(s) => s.id}
        searchPlaceholder="Search by name or SKU…"
        pageSize={10}
        emptyTitle={stockItems.length === 0 ? "No products in stock" : "No matching items"}
        emptyDescription={stockItems.length === 0 ? "Scan an invoice to add products to inventory." : undefined}
        columns={[
          { key: "_name",     header: "Product",  render: (s) => <strong>{s.product?.name || "Unknown"}</strong> },
          { key: "_sku",      header: "SKU",      render: (s) => <span className="t-mono t-muted">{s.product?.sku || "—"}</span> },
          { key: "_category", header: "Category", render: (s) => s.product?.category?.name || "—" },
          {
            key: "quantity",  header: "Quantity", align: "right",
            render: (s) => <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}>{s.quantity}</span>,
            searchable: false,
          },
          {
            key: "status", header: "Status",
            render: (s) =>
              (s.quantity <= (s.minStockLevel || 5))
                ? <StatusBadge tone="warning">LOW STOCK</StatusBadge>
                : <StatusBadge tone="success">IN STOCK</StatusBadge>,
            searchable: false,
          },
          {
            key: "actions", header: "", align: "right", searchable: false,
            render: (s) => (
              <div style={{ display: "inline-flex", gap: 6 }}>
                <Button variant="secondary" size="sm" onClick={() => openAdjustModal(s)}>Adjust</Button>
                <Button variant="ghost" size="sm" onClick={() => navigate(`${movementsBasePath}?productId=${s.product?.id}`)}>Movements</Button>
              </div>
            ),
          },
        ]}
      />

      <PremiumModal
        isOpen={adjustModal}
        toggle={() => setAdjustModal(false)}
        title={`Adjust stock: ${selectedItem?.product?.name || ""}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAdjustModal(false)}>Cancel</Button>
            <Button variant="primary" loading={adjSubmitting} onClick={handleAdjustSubmit}>Submit adjustment</Button>
          </>
        }
      >
        <div style={{ display: "grid", gap: 16 }}>
          <Field
            label="Quantity change"
            hint="Use + to add, − to remove (decimals OK)"
            required type="number" step="0.001"
            value={adjForm.quantityChange}
            onChange={(e) => setAdjForm({ ...adjForm, quantityChange: e.target.value })}
            placeholder="e.g. -2.5 or +3"
          />
          <SelectField
            label="Reason" required
            value={adjForm.reason}
            onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })}
          >
            <option value="Damaged">Damaged</option>
            <option value="Expired">Expired</option>
            <option value="Inventory Count">Inventory count</option>
            <option value="Other">Other</option>
          </SelectField>
          <Field
            label="Note" as="textarea" rows={3}
            value={adjForm.note}
            onChange={(e) => setAdjForm({ ...adjForm, note: e.target.value })}
            placeholder="Additional details (optional)…"
          />
        </div>
      </PremiumModal>
    </PageContainer>
  );
};

export default Inventory;
